import { TriangleAlert } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { DriverRoomIssues } from "@/hooks/useDriverRoomIssues"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { formatMinutesAgo } from "@/lib/i18n/format"

type Props = {
  issues: DriverRoomIssues
  className?: string
  size?: "sm" | "md"
}

export const buildIssueLines = (
  issues: DriverRoomIssues,
  t: TFunction,
): string[] => {
  const lines: string[] = []
  if (issues.gpsNotConfigured) lines.push(t("driverIssues.gpsNotConfigured"))
  if (issues.gpsNeverReceived) lines.push(t("driverIssues.gpsNeverReceived"))
  if (issues.gpsStale) {
    if (issues.lastReceivedAt !== undefined) {
      const minutesAgo = Math.round(
        (Date.now() - issues.lastReceivedAt) / 60_000,
      )
      lines.push(
        t("driverIssues.gpsStaleSince", {
          ago: formatMinutesAgo(t, minutesAgo),
        }),
      )
    } else {
      lines.push(t("driverIssues.gpsStale"))
    }
  }
  if (issues.carNotConfigured) lines.push(t("driverIssues.carNotConfigured"))
  return lines
}

const DriverRoomIssueIndicator = ({
  issues,
  className,
  size = "md",
}: Props) => {
  const { t } = useTranslation()
  if (!issues.hasAnyIssue) return null

  const lines = buildIssueLines(issues, t)
  const titleText = lines.join("\n")
  const iconSizeClass = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={t("driverIssues.warning")}
            title={titleText}
            className={cn("inline-flex items-center text-amber-500", className)}
          >
            <TriangleAlert className={iconSizeClass} />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <ul className="space-y-1 text-xs">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default DriverRoomIssueIndicator
